import { useState } from 'react';
import Sidebar from '../home/components/Sidebar';
import Navbar from '../home/components/Navbar';
import {
  getVideoUnderstandingModelConfig,
  analyzeFramesForPrompts,
  type ViralReversePrompt,
} from '../../services/viralVideoReverse';

const MAX_VIDEO_SIZE_MB = 20;
const MAX_VIDEO_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;

async function extractVideoFrames(file: File, frameCount: number = 4): Promise<string[]> {
  const objectUrl = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.src = objectUrl;
  video.muted = true;
  video.playsInline = true;
  video.crossOrigin = 'anonymous';

  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = () => reject(new Error('读取视频元数据失败'));
  });

  const duration = Math.max(1, Math.floor(video.duration || 1));
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    URL.revokeObjectURL(objectUrl);
    throw new Error('画布初始化失败');
  }

  const width = video.videoWidth || 720;
  const height = video.videoHeight || 1280;
  const maxSide = 960;
  const scale = Math.min(1, maxSide / Math.max(width, height));
  canvas.width = Math.max(320, Math.round(width * scale));
  canvas.height = Math.max(320, Math.round(height * scale));

  const frames: string[] = [];
  const points = Array.from({ length: frameCount }, (_, i) => {
    const ratio = (i + 1) / (frameCount + 1);
    return Math.min(duration - 0.1, Math.max(0, duration * ratio));
  });

  for (const t of points) {
    await new Promise<void>((resolve) => {
      const onSeeked = () => {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        frames.push(canvas.toDataURL('image/jpeg', 0.82));
        video.removeEventListener('seeked', onSeeked);
        resolve();
      };
      video.addEventListener('seeked', onSeeked);
      video.currentTime = t;
    });
  }

  URL.revokeObjectURL(objectUrl);
  return frames;
}

export default function ViralVideoReversePage() {
  const [activeMenu, setActiveMenu] = useState('爆款视频反推');
  const [uploadedVideo, setUploadedVideo] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedPrompts, setGeneratedPrompts] = useState<ViralReversePrompt[]>([]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (uploadedVideo) URL.revokeObjectURL(uploadedVideo);
      setError(null);
      setGeneratedPrompts([]);
      const videoUrl = URL.createObjectURL(file);
      setUploadedVideo(videoUrl);
      setUploadedFile(file);
    }
  };

  const handleAnalyze = async () => {
    if (!uploadedVideo || !uploadedFile) return;
    if (uploadedFile.size > MAX_VIDEO_BYTES) {
      setError(`视频建议不超过 ${MAX_VIDEO_SIZE_MB}MB，当前约 ${(uploadedFile.size / 1024 / 1024).toFixed(1)}MB`);
      return;
    }
    setError(null);
    setIsAnalyzing(true);
    try {
      const config = await getVideoUnderstandingModelConfig();
      const frames = await extractVideoFrames(uploadedFile, 4);
      const list = await analyzeFramesForPrompts(frames, config);
      setGeneratedPrompts(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : '视频反推失败，请重试');
      setGeneratedPrompts([]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCopyPrompt = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
  };

  const handleCopyAllPrompts = () => {
    const allPrompts = generatedPrompts.map(p => 
      `分镜${p.id}（${p.cameraWork}） ${p.timeRange}\n提示词：${p.prompt}`
    ).join('\n\n');
    navigator.clipboard.writeText(allPrompts);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex min-w-0">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        
        <main className="flex-1 main-content-padding overflow-y-auto overflow-x-hidden min-w-0">
          <div className="max-w-7xl mx-auto w-full min-w-0">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">爆款视频反推</h1>
              <p className="text-gray-400 text-sm">
                上传爆款视频，一键反推生成提示词，快速复刻热门作品风格。
              </p>
              {error && (
                <div className="mt-3 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {error}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
              {/* Left Panel - Upload & Analysis */}
              <div className="space-y-6 min-w-0">
                {/* Upload Section */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <h2 className="text-lg font-semibold">上传爆款视频</h2>
                  </div>

                  {/* Upload Area */}
                  <div className="relative">
                    <input
                      type="file"
                      accept="video/mp4,video/webm"
                      onChange={handleFileUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div className="border-2 border-dashed border-gray-700 rounded-xl p-12 text-center hover:border-blue-500 transition-colors bg-[#0a0a0a]">
                      <div className="w-16 h-16 bg-blue-600/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-upload-cloud-line text-blue-500 text-3xl"></i>
                      </div>
                      <p className="text-gray-300 mb-2">拖拽爆款视频到此处，或点击上传</p>
                      <p className="text-gray-500 text-sm">支持 MP4、WebM 格式，建议不超过 20MB</p>
                    </div>
                  </div>

                  {uploadedVideo && (
                    <div className="mt-4 min-w-0 max-w-full">
                      <video
                        src={uploadedVideo}
                        controls
                        className="w-full max-w-full rounded-lg bg-black"
                      />
                    </div>
                  )}
                </div>

                {/* Analysis Button - 始终显示 */}
                <button
                  onClick={handleAnalyze}
                  disabled={!uploadedVideo || isAnalyzing}
                  className={`w-full py-4 rounded-xl font-semibold text-lg transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-3 ${
                    !uploadedVideo
                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                      : isAnalyzing
                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40'
                  }`}
                >
                  {isAnalyzing ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xl"></i>
                      <span>反推提示词中...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-magic-line text-xl"></i>
                      <span>反推提示词</span>
                      <span className="bg-yellow-500 text-black text-xs px-2 py-1 rounded-full ml-2">15 点</span>
                    </>
                  )}
                </button>

                {/* Video Analysis Info */}
                {generatedPrompts.length > 0 && (
                  <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">视频分析</h3>
                      <span className="text-xs text-green-400 flex items-center gap-1">
                        <i className="ri-checkbox-circle-fill"></i>
                        分析完成
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="bg-[#0a0a0a] rounded-lg p-4 border border-gray-800">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-movie-line text-blue-400"></i>
                          <span className="text-sm font-medium text-gray-300">视频风格</span>
                        </div>
                        <p className="text-sm text-gray-400">现代商务风格，电影感镜头语言，专业商业广告质感</p>
                      </div>

                      <div className="bg-[#0a0a0a] rounded-lg p-4 border border-gray-800">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-time-line text-purple-400"></i>
                          <span className="text-sm font-medium text-gray-300">时长分析</span>
                        </div>
                        <p className="text-sm text-gray-400">总时长 35 秒，建议分为 9 个分镜，每个分镜 3-5 秒</p>
                      </div>

                      <div className="bg-[#0a0a0a] rounded-lg p-4 border border-gray-800">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-camera-line text-green-400"></i>
                          <span className="text-sm font-medium text-gray-300">镜头运用</span>
                        </div>
                        <p className="text-sm text-gray-400">航拍、跟拍、特写、环绕等多种镜头组合，节奏流畅</p>
                      </div>

                      <div className="bg-[#0a0a0a] rounded-lg p-4 border border-gray-800">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-lightbulb-line text-orange-400"></i>
                          <span className="text-sm font-medium text-gray-300">核心要素</span>
                        </div>
                        <p className="text-sm text-gray-400">城市场景、商务人物、现代办公、产品展示、品牌收尾</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tips */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-blue-400 text-xl mt-0.5"></i>
                    <div>
                      <h3 className="text-sm font-semibold mb-2">温馨提示</h3>
                      <ul className="text-xs text-gray-400 space-y-2">
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-blue-400 mt-0.5"></i>
                          <span>上传清晰度高、内容完整的爆款视频，系统会更准确地反推提示词。</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-blue-400 mt-0.5"></i>
                          <span>反推得到的提示词可直接用于视频生成，快速复刻热门作品风格。</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-blue-400 mt-0.5"></i>
                          <span>不同视频风格的关键要素不同，可以多个爆款视频对比，找出爆款规律。</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Panel - Generated Prompts */}
              <div className="space-y-6 min-w-0">
                {generatedPrompts.length > 0 ? (
                  <>
                    {/* Header */}
                    <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold">生成的提示词</h2>
                        <button 
                          onClick={handleCopyAllPrompts}
                          className="px-4 py-2 bg-[#0a0a0a] border border-gray-700 text-gray-300 text-sm rounded-lg hover:border-blue-500 hover:text-blue-400 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                        >
                          <i className="ri-file-copy-line"></i>
                          <span>复制全部</span>
                        </button>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-[#0a0a0a] rounded-lg p-3 border border-gray-800">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-blue-600/10 rounded-lg flex items-center justify-center">
                              <i className="ri-file-text-line text-blue-400 text-sm"></i>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">提示词数量</p>
                              <p className="text-lg font-bold">{generatedPrompts.length}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-[#0a0a0a] rounded-lg p-3 border border-gray-800">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-purple-600/10 rounded-lg flex items-center justify-center">
                              <i className="ri-movie-line text-purple-400 text-sm"></i>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">分镜数量</p>
                              <p className="text-lg font-bold">{generatedPrompts.length}个</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Prompts List */}
                    <div className="space-y-4 max-h-[800px] overflow-y-auto pr-2 custom-scrollbar">
                      {generatedPrompts.map((item) => (
                        <div
                          key={item.id}
                          className="bg-[#1a1a1a] rounded-xl p-5 border border-gray-800 hover:border-blue-500/50 transition-all group"
                        >
                          {/* Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                <span className="text-white text-sm font-bold">{item.id}</span>
                              </div>
                              <div>
                                <h3 className="text-white font-semibold text-sm">{item.scene}</h3>
                                <p className="text-gray-500 text-xs">{item.timeRange}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleCopyPrompt(item.prompt)}
                              className="w-8 h-8 bg-[#0a0a0a] border border-gray-700 rounded-lg flex items-center justify-center hover:border-blue-500 hover:text-blue-400 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                              title="复制提示词"
                            >
                              <i className="ri-file-copy-line text-sm"></i>
                            </button>
                          </div>

                          {/* Camera Work */}
                          <div className="flex items-center gap-2 mb-3">
                            <i className="ri-camera-line text-purple-400 text-sm"></i>
                            <span className="text-xs text-gray-400">镜头运用：</span>
                            <span className="text-xs text-purple-400 font-medium">{item.cameraWork}</span>
                          </div>

                          {/* Prompt */}
                          <div className="bg-[#0a0a0a] rounded-lg p-4 border border-gray-800">
                            <div className="flex items-start gap-2 mb-2">
                              <i className="ri-quill-pen-line text-blue-400 text-sm mt-0.5"></i>
                              <span className="text-xs text-gray-400 font-medium">提示词：</span>
                            </div>
                            <p className="text-sm text-gray-300 leading-relaxed pl-6">
                              {item.prompt}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="bg-[#1a1a1a] rounded-2xl p-12 border border-gray-800 text-center h-full flex flex-col items-center justify-center min-h-[600px]">
                    <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-file-text-line text-gray-600 text-4xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">上传爆款视频开始反推</h3>
                    <p className="text-gray-400 text-sm">
                      提示词将自动生成，可直接用于视频创作
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #0a0a0a;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4b5563;
        }
      `}</style>
    </div>
  );
}
